import confetti from 'canvas-confetti';
import { Icon } from 'components/Icon';
import { X } from 'lucide-static';
import { delayMs, lateinit } from 'shared/util';

export function showConfetti() {
	const count = 200;
	const defaults = {
		origin: { y: 0.7 },
	};

	function fire(particleRatio: number, opts: confetti.Options | undefined) {
		return confetti({
			...defaults,
			...opts,
			particleCount: Math.floor(count * particleRatio),
		});
	}

	return Promise.allSettled([
		fire(0.25, { spread: 26, startVelocity: 55 }),
		fire(0.2, { spread: 60 }),
		fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 }),
		fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 }),
		fire(0.1, { spread: 120, startVelocity: 45 }),
	]);
}

export function showRedFlash() {
	const redFlash: HTMLElement = <div class="fixed inset-0 z-50 bg-red-600"></div>;
	document.body.append(redFlash);

	return redFlash
		.animate([{ opacity: '0' }, { opacity: '1', offset: 0.1 }, { opacity: '0' }], {
			duration: 2000,
			easing: 'ease-out',
			fill: 'forwards',
		})
		.finished.then(() => redFlash.remove());
}

type ToastProps = {
	icon?: string;
	title: string;
	actions?: { name: string; action: () => void }[];
	closable?: boolean;
};
export function Toast({ icon, title, actions, closable }: ToastProps) {
	const view = (
		<div class="flex w-fit items-center gap-4 rounded-md border border-gray-200 bg-white px-4 py-2 shadow-md">
			{icon && <Icon icon={icon} class="size-5 text-gray-700" />}
			<span class="text-gray-800">{title}</span>
			{(actions ?? []).map(({ name, action }) => (
				<button class="rounded-md px-2 py-1 text-sm font-bold hover:bg-gray-100" on:click={action}>
					{name}
				</button>
			))}
			{closable && (
				<button class="rounded-full p-1 opacity-95 hover:bg-gray-100" on:click={() => view.remove()}>
					<Icon icon={X} class="size-3" />
				</button>
			)}
		</div>
	);
	return view;
}

let toastContainer: HTMLElement = lateinit;

export function showToast(toast: HTMLElement, duration: number = -1) {
	if (!toastContainer) {
		toastContainer = <div class="pointer-events-none absolute inset-0 grid grid-cols-1 content-end justify-items-end gap-3 p-4"></div>;
		document.body.append(toastContainer);
	}

	const toastWrap = (
		<div style="pointer-events: all;" class="origin-bottom">
			{toast}
		</div>
	);

	toastContainer.append(toastWrap);
	const intrinsicHeight = `${toast.scrollHeight}px`; // Get intrinsic height

	toastWrap.animate(
		[
			{ height: '0', opacity: '.6' },
			{ height: intrinsicHeight, opacity: '1' },
		],
		{
			duration: 300,
			easing: 'cubic-bezier(.16,.04,0,.96)',
			fill: 'forwards', // Ensure it stays at final state
		},
	);

	if (duration > -1) {
		delayMs(duration).then(() => {
			toastWrap
				.animate(
					[
						{ opacity: '1', scale: '1', translate: '0 0rem' },
						{ opacity: '0', scale: '0.8', translate: '0 3rem' },
					],
					{
						duration: 200,
						easing: 'cubic-bezier(.86,.02,.96,.68)',
					},
				)
				.finished.then(() => toastWrap.remove());
		});
	}
}
